import * as EmailValidator from 'email-validator'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import React, {useReducer} from 'react'
import styled, {css} from 'styled-components'

import Button from '@/components/button'
import {Consents} from '@/components/consents'
import Input from '@/components/input'
import PhoneInput from '@/components/phone-input'
import Select from '@/components/select'
import {Text} from '@/components/text'
import {useConsents} from '@/hooks/use-consents'
import useFormErrors from '@/hooks/use-form-errors'
import {useGetCountryName} from '@/hooks/use-get-country-name'
import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import useNavigate from '@/hooks/use-navigate'
import useParams from '@/hooks/use-params'
import {useSessionStorage} from '@/hooks/use-storage'
import {useSubmitHubspotForm} from '@/hooks/use-submit-hubspot-form'
import locales from '@/locale/locales'
import {useGlobalStrings} from '@/providers/global-strings'
import {useGoogleReCaptcha} from '@/providers/google-recaptcha'
import {useLocale} from '@/providers/locale'
import {container} from '@/styles/grid'
import {theme} from '@/theme'
import CompanySize from '@/types/company-size'
import DiscoveredVia from '@/types/discovered-via'
import {arrayShuffle} from '@/utils/array-shuffle'
import {isBrowser} from '@/utils/browser'
import {getCompanySizeOptions} from '@/utils/get-company-size-options'
import {isGenericEmail} from '@/utils/is-generic-email'
import mq from '@/utils/media-query'
import {scrollToTop} from '@/utils/scroll'

import type {CountryCode, LanguageCode} from '@/locale/locales'
import type {MeetingBookerParams} from '@/templates/meeting-booker/meeting-booker'
import type {FormConfig, ImperativeConsent} from '@/types/consents'
import type {HubspotContactProps} from '@/types/hubspot-contact-props'
import type {CountryCode as PhoneCountryCode} from 'libphonenumber-js'

export const OTHER = 'Other' as const

export interface State {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    phoneCountryCode: PhoneCountryCode
    companySize: CompanySize
    country: CountryCode | typeof OTHER | ''
    discoveredVia: DiscoveredVia | ''
    utmCampaign: string
    latestUtmCampaign: string
    hasConsented: boolean
}

enum ActionType {
    SET_VALUE,
}

type SetValue<TKey extends keyof State> = {
    type: ActionType.SET_VALUE
    key: keyof State
    value: State[TKey]
}

type Action<TKey extends keyof State> = SetValue<TKey>

const reducer = <TKey extends keyof State>(state: State, action: Action<TKey>) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const useValidateInputs = (hiddenFields: Array<Fields>) => {
    const globalStrings = useGlobalStrings()

    return (state: State, isValidEmail: boolean) => {
        type Error = {
            key: keyof State
            value: string
        }

        const errors: Error[] = []

        const parsedPhoneNumber = parsePhoneNumberFromString(
            state.phoneNumber,
            state.phoneCountryCode,
        )
        const isValidPhoneNumber = !!parsedPhoneNumber?.isValid()

        if (!state.firstName.trim() && !hiddenFields.includes('firstName')) {
            errors.push({
                key: 'firstName',
                value: globalStrings.first_name_error,
            })
        }

        if (!state.lastName.trim() && !hiddenFields.includes('lastName')) {
            errors.push({
                key: 'lastName',
                value: globalStrings.last_name_error,
            })
        }

        if (
            (!state.email.trim() || !isValidEmail || !EmailValidator.validate(state.email)) &&
            !hiddenFields.includes('email')
        ) {
            errors.push({
                key: 'email',
                value: !isValidEmail
                    ? globalStrings.please_enter_your_work_email
                    : globalStrings.invalid_email_error,
            })
        }

        if (
            (!state.phoneNumber.trim() || !isValidPhoneNumber) &&
            !hiddenFields.includes('phoneNumber')
        ) {
            errors.push({
                key: 'phoneNumber',
                value: globalStrings.phone_number_error,
            })
        }

        if (!state.companySize.trim() && !hiddenFields.includes('companySize')) {
            errors.push({
                key: 'companySize',
                value: globalStrings.company_size_error,
            })
        }

        if (!state.country.trim() && !hiddenFields.includes('country')) {
            errors.push({
                key: 'country',
                value: globalStrings.country_error,
            })
        }

        return errors
    }
}

export enum RedirectType {
    GO_TO_THANK_YOU_SOLE_TRADER,
    GO_TO_MEETING_BOOKER,
    GO_TO_THANK_YOU_PAGE,
}

type Navigate = ReturnType<typeof useNavigate>

const handleRedirectEffect =
    (
        state: State,
        navigate: Navigate,
        language: LanguageCode,
        setSessionValue: (value: MeetingBookerParams) => void,
        redirectTo?: string,
    ) =>
    async (redirectType: RedirectType) => {
        switch (redirectType) {
            case RedirectType.GO_TO_MEETING_BOOKER: {
                if (state.country === '') {
                    return
                }

                // Makes sure build passes as session storage doesn't exist on build
                if (isBrowser) {
                    const parsedPhoneNumber = parsePhoneNumberFromString(
                        state.phoneNumber,
                        state.phoneCountryCode,
                    )?.number

                    const meetingBookerParams: MeetingBookerParams = {
                        source: 'demo-form',
                        firstName: state.firstName,
                        lastName: state.lastName,
                        email: state.email,
                        phoneNumber: parsedPhoneNumber,
                        companySize: state.companySize,
                        country: state.country === 'Other' ? 'gb' : state.country,
                        language,
                    }

                    setSessionValue(meetingBookerParams)
                }

                return navigate(`/book-a-demo`)
            }

            case RedirectType.GO_TO_THANK_YOU_SOLE_TRADER: {
                return navigate('/thankyousoletrader')
            }

            case RedirectType.GO_TO_THANK_YOU_PAGE: {
                return navigate(redirectTo!)
            }

            default: {
                throw new Error('Unrecognized redirect type')
            }
        }
    }

export const getRedirectType = (
    companySize: State['companySize'],
    country: State['country'],
    redirectTo?: string,
) => {
    const isOfSizes = (sizes: State['companySize'][]) => sizes.includes(companySize)
    const isOfCountries = (countries: State['country'][]) => countries.includes(country)

    const {SoleTrader} = CompanySize

    if (redirectTo) {
        return RedirectType.GO_TO_THANK_YOU_PAGE
    }

    if (isOfSizes([SoleTrader]) && !isOfCountries(['dk', 'se'])) {
        return RedirectType.GO_TO_THANK_YOU_SOLE_TRADER
    }

    return RedirectType.GO_TO_MEETING_BOOKER
}

const getCountry = ({
    urlParamCountry,
    currentCountry,
}: {
    urlParamCountry: string
    currentCountry: CountryCode
}) => {
    if (urlParamCountry && locales.findIndex((locale) => locale.country === urlParamCountry)) {
        return urlParamCountry as CountryCode
    }

    return currentCountry
}

type Fields =
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phoneNumber'
    | 'country'
    | 'companySize'
    | 'discoveredVia'

const bgColor = {
    muted: theme.colors.background.muted,
    default: theme.colors.background.default,
}

type Variant = keyof typeof bgColor

export const isAllowedBgColor = (color: string): color is Variant =>
    Object.keys(bgColor).includes(color)

export interface Props {
    hubspotProperties?: Partial<HubspotContactProps>
    redirectCallback?: (state: State) => void
    shouldRedirect?: boolean
    className?: string
    formConfig: FormConfig
    imperativeConsents?: ImperativeConsent[]
    content: {
        preTitle?: string
        title?: string
        submitButtonText?: string
        disclaimer?: string
    }
    hiddenFields?: Array<Fields>
    onFormReached?: () => void
    onFormInteracted?: (key: keyof State) => void
    onSubmitSuccess?: (state: State) => void
    onBeforeSubmit?: () => Promise<object>
    variant?: Variant
    redirectTo?: string
}

export const HubspotForm = ({
    content,
    hubspotProperties,
    redirectCallback,
    shouldRedirect = true,
    className,
    formConfig,
    imperativeConsents = [],
    hiddenFields = [],
    onFormReached,
    onFormInteracted,
    onSubmitSuccess,
    onBeforeSubmit,
    redirectTo,
    variant = 'default',
}: Props) => {
    const currentLocale = useLocale()
    const getCountryName = useGetCountryName()
    const params = useParams()
    const validateInputs = useValidateInputs(hiddenFields)
    const [submissionError, setSubmissionError] = React.useState('')
    const {isLoading, recaptchaHubspotSubmit} = useSubmitHubspotForm(
        formConfig.formId || 'dummy',
        false,
    )
    const {consentsProps, consentsPayload, validateConsents} = useConsents({
        ...formConfig.consents,
        imperativeConsents,
    })
    const companySizeOptions = getCompanySizeOptions()

    React.useEffect(() => {
        onFormReached && onFormReached()
    }, [onFormReached])

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        setSubmissionError('')

        // Validate inputs
        const errors = validateInputs(state, isValidEmail)

        if (!validateConsents()) {
            errors.push({
                key: 'hasConsented',
                value: 'Please accept all consents',
            })
        }

        if (errors.length) {
            errors.forEach((error) => {
                setError(error.key, error.value)
            })

            return scrollToTop()
        }

        try {
            await recaptchaHubspotSubmit(
                {
                    ...state,
                    genericEmail: isGenericEmail(state.email),
                    ...hubspotProperties,
                },
                consentsPayload,
                () => {
                    setSubmissionError(globalStrings.something_went_wrong)
                },
                () => onSubmitSuccess && onSubmitSuccess(state),
                () => onBeforeSubmit && onBeforeSubmit(),
            )
        } catch (error) {
            setSubmissionError(globalStrings.something_went_wrong)
        }

        // Redirect user
        if (shouldRedirect) {
            if (redirectCallback) {
                redirectCallback(state)
            } else {
                redirect(getRedirectType(state.companySize, state.country, redirectTo))
            }
        }
    }

    const countryCode = getCountry({
        urlParamCountry: params.get('country') || '',
        currentCountry: currentLocale.country,
    })

    const initialState: State = {
        firstName: '',
        lastName: '',
        email: params.get('email') || '',
        phoneNumber: '',
        phoneCountryCode: countryCode.toUpperCase() as PhoneCountryCode,
        companySize: '' as CompanySize,
        country: countryCode,
        discoveredVia: '',
        utmCampaign: params.get('utm_campaign') || '',
        latestUtmCampaign: params.get('latest_utm_campaign') || '',
        hasConsented: false,
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const {errors, setError, clearError} = useFormErrors(state)
    const isValidEmail = useIsCompanyEmail(state.email || '')
    const navigate = useNavigate({rawPath: !!redirectTo})
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [_, setSessionValue] = useSessionStorage<MeetingBookerParams>('meetingBookerParams', {})
    const redirect = handleRedirectEffect(
        state,
        navigate,
        currentLocale.language,
        setSessionValue,
        redirectTo,
    )
    const globalStrings = useGlobalStrings()
    const {loadReCaptcha} = useGoogleReCaptcha()

    const setValue = <TKey extends keyof State>(key: TKey, value: State[TKey]) => {
        dispatch({
            type: ActionType.SET_VALUE,
            key,
            value,
        })

        onFormInteracted && onFormInteracted(key)
    }

    const uniqueCountryCodes = Array.from(new Set(locales.map((locale) => locale.country)))

    const countryOptions = [
        ...uniqueCountryCodes.map((countryCode) => ({
            label: getCountryName(countryCode),
            value: countryCode,
        })),
        {
            label: globalStrings.other,
            value: OTHER,
        },
    ]

    const discoveredViaOptions = [
        {
            label: globalStrings.peer_recommendation,
            value: DiscoveredVia.PeerRecommendation,
        },
        {
            label: globalStrings.trust_pilot,
            value: DiscoveredVia.TrustpilotG2orCapterra,
        },
        {
            label: globalStrings.google_search,
            value: DiscoveredVia.GoogleSearchEngine,
        },
        {
            label: globalStrings.youtube,
            value: DiscoveredVia.YouTube,
        },
        {
            label: globalStrings.tv,
            value: DiscoveredVia.TV,
        },
        {
            label: globalStrings.outdoor_advertising,
            value: DiscoveredVia.OutdoorAdvertising,
        },
        {
            label: globalStrings.social_media,
            value: DiscoveredVia.SocialMedia,
        },
        {
            label: globalStrings.press,
            value: DiscoveredVia.Press,
        },
    ]

    const discoveredViaOptionsShuffled = arrayShuffle(discoveredViaOptions)

    const handleInputChange: typeof setValue = (key, value) => {
        loadReCaptcha()
        clearError(key)
        setValue(key, value)
    }

    const errorSpacePlaceholder = <Text $variant="label-base">{'\u00a0'}</Text>

    const showFirstName = !hiddenFields.includes('firstName')
    const showLastName = !hiddenFields.includes('lastName')
    const showEmail = !hiddenFields.includes('email')
    const showPhoneNumber = !hiddenFields.includes('phoneNumber')
    const showCompanySize = !hiddenFields.includes('companySize')
    const showCountry = !hiddenFields.includes('country')
    const showDiscoveredVia = !hiddenFields.includes('discoveredVia')

    const inputBgColor = variant === 'default' ? 'muted' : 'default'

    return (
        <Form className={className} onSubmit={handleFormSubmit} noValidate $variant={variant}>
            {content.preTitle ||
                (content.title && (
                    <Titles>
                        {content.preTitle && (
                            <Text $variant="paragraph-base" $color="muted" $center>
                                {content.preTitle}
                            </Text>
                        )}

                        {content.title && (
                            <Text $variant="title-base" $center>
                                {content.title}
                            </Text>
                        )}
                    </Titles>
                ))}

            <InputsWrapper $variant={variant}>
                {(showFirstName || showLastName) && (
                    <Row $variant={variant}>
                        {showFirstName && (
                            <div>
                                <StyledInput
                                    label={globalStrings.first_name}
                                    value={state.firstName}
                                    onChange={(value) => handleInputChange('firstName', value)}
                                    error={errors.firstName}
                                    required
                                    data-testid="hubspot-first-name-input"
                                    $background={inputBgColor}
                                />

                                {!errors.firstName && errorSpacePlaceholder}
                            </div>
                        )}

                        {showLastName && (
                            <div>
                                <StyledInput
                                    label={globalStrings.last_name}
                                    value={state.lastName}
                                    onChange={(value) => handleInputChange('lastName', value)}
                                    error={errors.lastName}
                                    required
                                    data-testid="hubspot-last-name-input"
                                    $background={inputBgColor}
                                />

                                {!errors.lastName && errorSpacePlaceholder}
                            </div>
                        )}
                    </Row>
                )}

                {showEmail && (
                    <Row $variant={variant}>
                        <div>
                            <StyledInput
                                type="email"
                                label={globalStrings.work_email}
                                value={state.email}
                                onChange={(value) => handleInputChange('email', value)}
                                error={errors.email}
                                required
                                data-testid="hubspot-email-input"
                                $background={inputBgColor}
                            />

                            {!errors.email && errorSpacePlaceholder}
                        </div>
                    </Row>
                )}

                {showPhoneNumber && (
                    <Row $variant={variant}>
                        <div>
                            <StyledPhoneInput
                                label={globalStrings.phone_number}
                                setPhoneNumber={(value) => handleInputChange('phoneNumber', value)}
                                phoneNumber={state.phoneNumber}
                                setCountryCode={(value) =>
                                    handleInputChange('phoneCountryCode', value)
                                }
                                countryCode={state.phoneCountryCode}
                                error={errors.phoneNumber}
                                required
                                data-testid="hubspot-phone-number-input"
                                $background={inputBgColor}
                            />

                            {!errors.phoneNumber && errorSpacePlaceholder}
                        </div>
                    </Row>
                )}

                {(showCompanySize || showCountry) && (
                    <Row $variant={variant}>
                        {showCompanySize && (
                            <div>
                                <StyledSelect
                                    label={globalStrings.company_size}
                                    options={companySizeOptions}
                                    placeholder={globalStrings.select}
                                    value={state.companySize}
                                    onChange={(value) =>
                                        handleInputChange('companySize', value as CompanySize)
                                    }
                                    error={errors.companySize}
                                    required
                                    data-testid="hubspot-company-size-select"
                                    $background={inputBgColor}
                                />

                                {!errors.companySize && errorSpacePlaceholder}
                            </div>
                        )}

                        {showCountry && (
                            <div>
                                <StyledSelect
                                    label={globalStrings.country}
                                    options={countryOptions}
                                    placeholder={globalStrings.country_placeholder}
                                    value={state.country}
                                    onChange={(value) =>
                                        handleInputChange('country', value as CountryCode)
                                    }
                                    error={errors.country}
                                    required
                                    data-testid="hubspot-country-select"
                                    $background={inputBgColor}
                                />

                                {!errors.country && errorSpacePlaceholder}
                            </div>
                        )}
                    </Row>
                )}

                {showDiscoveredVia && (
                    <Row $variant={variant}>
                        <div>
                            <StyledSelect
                                label={globalStrings.discovered_via_label}
                                options={discoveredViaOptionsShuffled}
                                placeholder={globalStrings.discovered_via_placeholder}
                                value={state.discoveredVia}
                                onChange={(value) =>
                                    handleInputChange('discoveredVia', value as DiscoveredVia)
                                }
                                data-testid="hubspot-discovered-via-select"
                                $background={inputBgColor}
                            />
                        </div>
                    </Row>
                )}
            </InputsWrapper>

            <Consents {...consentsProps} />

            <div>
                <Button
                    type="submit"
                    loading={isLoading}
                    data-testid="hubspot-submit-button"
                    style={{width: '100%'}}
                >
                    {content.submitButtonText ?? globalStrings.demo_submit_text}
                </Button>

                {content.disclaimer && (
                    <StyledText $variant="label-base">{content.disclaimer}</StyledText>
                )}

                {submissionError && (
                    <Text
                        $variant="label-base"
                        $color="error"
                        style={{marginTop: theme.spacings.s16}}
                    >
                        {submissionError}
                    </Text>
                )}
            </div>
        </Form>
    )
}

const StyledText = styled(Text)`
    margin-top: ${({theme}) => theme.spacings.s12};
`

const wrapperStyles = css<{$variant: Variant}>`
    ${container}
    display: grid;
    row-gap: ${({theme}) => theme.spacings.s32};
    width: 100%;
    padding: ${({theme}) => theme.spacings.s20};
    background: ${({$variant}) =>
        $variant === 'default' ? theme.colors.background.default : theme.colors.background.muted};
    border-radius: ${({$variant}) => ($variant === 'default' ? 0 : theme.radii.arc8)};

    ${({$variant}) => mq.min($variant === 'default' ? 'xs' : 'md')} {
        max-width: 600px;
        padding: ${({theme}) => `${theme.spacings.s28} ${theme.spacings.s40}`};
        border-radius: ${({theme}) => theme.radii.arc8};
    }
`

const Form = styled.form<{$variant: Variant}>`
    ${wrapperStyles}
`

const StyledInput = styled(Input)<{$background: Variant}>`
    background: ${({$background}) => bgColor[$background]};
`

const StyledPhoneInput = styled(PhoneInput)<{$background: Variant}>`
    background: ${({$background}) => bgColor[$background]};
`

const StyledSelect = styled(Select)<{$background: Variant}>`
    button {
        background: ${({$background}) => bgColor[$background]};
    }
`

const Titles = styled.div`
    display: grid;
    row-gap: ${({theme}) => theme.spacings.s10};
`

const InputsWrapper = styled.div<{$variant: Variant}>`
    display: grid;
    gap: ${({theme}) => theme.spacings.s20};
    width: 100%;

    ${({$variant}) => mq.min($variant === 'default' ? 'xs' : 'md')} {
        row-gap: ${({theme}) => theme.spacings.s12};
    }
`

const Row = styled.div<{$variant: Variant}>`
    display: grid;
    gap: inherit;
    grid-template-columns: 1fr;

    ${({$variant}) => mq.min($variant === 'default' ? 'xs' : 'md')} {
        grid-auto-columns: 1fr;
        grid-auto-flow: column;
    }
`
